import { Box, Divider, HorizontalGrid, Text } from '@shopify/polaris';
import { VerticalSpace } from '../../../UI/Reports/VerticalSpace';
import { formatPct, formatUnits } from './formatters';

const EFFICACY_NUMBER = '#4A4A4F';
const SUBTLE = '#6D7175';
const STRONG = '#202223';
const RANK_WATERMARK = '#D1D5DB';
const PLACEHOLDER_BG = '#F6F6F7';

// Número de ranking — sin fondo, sin borde. Gris claro, grande, peso
// fuerte: queda como una filigrana en la izquierda de cada fila.
const RankNumber = ({ n }) => (
  <div
    style={{
      width: 28,
      fontSize: 32,
      fontWeight: 800,
      lineHeight: 1,
      color: RANK_WATERMARK,
      textAlign: 'center',
      flexShrink: 0,
      userSelect: 'none',
    }}
  >
    {n}
  </div>
);

const Stat = ({ label, value }) => (
  <span style={{ fontSize: 12, color: SUBTLE, lineHeight: 1.4 }}>
    {label}: <strong style={{ color: STRONG, fontWeight: 600 }}>{value}</strong>
  </span>
);

// `term` del DescriptionList: el TÍTULO de la sección (título +
// subtítulo subdued). Va a la columna izquierda, lo lee Polaris como
// la "etiqueta" del par.
const SectionTerm = ({ title, subtitle }) => (
  <div>
    <Text variant="headingMd" as="h3">
      {title}
    </Text>
    <Box minHeight=".15rem" />
    <Text variant="bodySm" tone="subdued">
      {subtitle}
    </Text>
  </div>
);

// Mini-tabla con las entradas de una columna (5 como mucho). Cada fila
// lleva el rank "filigrana", foto, family link, 3 stats inline y % de
// eficacia en gris oscuro. `startRank` permite que la segunda columna
// siga numerando desde 6 en lugar de reiniciar en 1.
const RankingTable = ({ entries, startRank = 1 }) => (
  <div>
    {entries.map((row, i) => (
      <div
        key={row.family}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          paddingTop: i === 0 ? 0 : 12,
          paddingBottom: i === entries.length - 1 ? 0 : 12,
          borderBottom: i === entries.length - 1 ? 'none' : '1px solid #F1F2F4',
        }}
      >
        <RankNumber n={startRank + i} />
        {row.image ? (
          <img
            src={row.image}
            alt={row.family}
            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
          />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 6, backgroundColor: PLACEHOLDER_BG, flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: STRONG, marginBottom: 4 }}>
            {row.productId ? (
              <a
                className="efftable-link"
                href={`/products/${row.productId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {row.family}
              </a>
            ) : (
              row.family
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px' }}>
            <Stat label="Entregado" value={`${formatUnits(row.buyUnits)} uds`} />
            <Stat label="Campaña" value={`${formatUnits(row.sellUnitsInSeason)} uds`} />
            <Stat label="Total" value={`${formatUnits(row.sellUnitsTotal)} uds`} />
          </div>
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: EFFICACY_NUMBER,
            minWidth: 60,
            textAlign: 'right',
            flexShrink: 0,
          }}
        >
          {formatPct(row.efficacyTotal)}
        </div>
      </div>
    ))}
  </div>
);

/**
 * Dos bloques — "Lo más eficaz" y "Lo menos eficaz" — con top 10 /
 * bottom 10 por eficacia total. Pensados para vivir bajo la tabla del
 * tab Series, cuando ésta no llena el viewport.
 *
 * Como son 10 entradas, cada bloque usa 3 columnas: la primera con el
 * título y la descripción, y las otras dos con 5 elementos cada una
 * (ranks 1-5 y 6-10).
 *
 * Filtra filas con `buyUnits === 0` o `efficacyTotal` no finito —
 * sin entregas no hay eficacia y meterlas distorsiona el ranking.
 * Si hay solapamiento (catálogo muy pequeño) excluye del bottom las
 * que ya están en el top.
 */
export const EfficacyHighlights = ({ rows }) => {
  const valid = (rows || []).filter(
    (r) => Number(r.buyUnits) > 0 && Number.isFinite(Number(r.efficacyTotal)),
  );
  if (valid.length === 0) return null;

  const sorted = [...valid].sort((a, b) => b.efficacyTotal - a.efficacyTotal);
  const top10 = sorted.slice(0, 10);
  const topSet = new Set(top10);
  const bottom10 = sorted
    .slice()
    .reverse()
    .filter((r) => !topSet.has(r))
    .slice(0, 10);

  // Layout 1fr 1fr 1fr — en xs se apila. La primera columna lleva el
  // título (con padding izquierdo para darle aire respecto al borde
  // del Card); las otras dos reparten las 10 entradas en 5 + 5.
  const Section = ({ term, entries }) => (
    <HorizontalGrid columns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap="4">
      <div style={{ paddingLeft: 24 }}>{term}</div>
      <RankingTable entries={entries.slice(0, 5)} startRank={1} />
      {entries.length > 5 && <RankingTable entries={entries.slice(5, 10)} startRank={6} />}
    </HorizontalGrid>
  );

  return (
    <div>
      <Section
        term={<SectionTerm title="Lo más eficaz" subtitle={`Top ${top10.length} por eficacia total`} />}
        entries={top10}
      />
      {bottom10.length > 0 && (
        <>
          <VerticalSpace />
          <Divider />
          <VerticalSpace />
          <Section
            term={<SectionTerm title="Lo menos eficaz" subtitle={`Las ${bottom10.length} con menor eficacia total`} />}
            entries={bottom10}
          />
        </>
      )}
    </div>
  );
};
